import React, { useState, useImperativeHandle, forwardRef, useEffect } from 'react';
import { openOverlay } from 'data/overlays/overlayHandler';
import { formatPrice } from '../../helpers/priceFormatHelper';
import { useDispatch } from 'react-redux';
import { IBuyBackConfig, IBuyBackProduct } from 'components/BuyBack/interfaces/buyBackInterfaces';
import RenderHTML from 'vkid-ui/lib/Components/RenderHTML';

interface IProps {
    buyBackConfig: IBuyBackConfig;
    setSkipBuyBack: (bool: boolean) => void;
    setAddBuyBackProducts: (bool: boolean) => void;
    skipBuyBack: boolean;
    addBuyBackProducts: boolean;
    buyBackProducts: IBuyBackProduct[];
}

export interface BuyBackDetailRef {
    validate: () => boolean;
}

const BuyBackDetail = forwardRef<BuyBackDetailRef, IProps>((props, ref) => {
    const {
        buyBackConfig,
        setSkipBuyBack,
        setAddBuyBackProducts,
        skipBuyBack,
        addBuyBackProducts,
        buyBackProducts,
    } = props;
    const dispatch = useDispatch();
    const [showValidation, setShowValidation] = useState(false);

    const hasValidationError =
        (!skipBuyBack && !addBuyBackProducts) || (addBuyBackProducts && buyBackProducts.length === 0);

    const hasError = showValidation && hasValidationError;

    const getErrorMessage = (): string => {
        if (!skipBuyBack && !addBuyBackProducts) {
            return buyBackConfig.labels.buybackSelectionError;
        }
        if (addBuyBackProducts && buyBackProducts.length === 0) {
            return buyBackConfig.labels.buybackNoDeviceError;
        }
        return '';
    };

    useImperativeHandle(
        ref,
        () => ({
            validate: () => {
                setShowValidation(true);
                return !hasValidationError;
            },
        }),
        [hasValidationError],
    );

    useEffect(() => {
        if (showValidation && !hasValidationError) {
            setShowValidation(false);
        }
    }, [hasValidationError, showValidation]);

    return (
        <>
            <fieldset className="tradein-dialog">
                <legend>
                    {buyBackConfig.labels.deviceToTradeinQuestion}{' '}
                    <span className="text-danger" aria-label="required">
                        *
                    </span>
                </legend>
                <p>
                    <RenderHTML html={buyBackConfig.labels.getMoneyBackLabel} nowrapper />
                </p>
                <p id="buyback-required">
                    <span className="text-danger">*</span> {buyBackConfig.labels.buybackSelectionRequired}
                </p>

                {hasError && (
                    <div className="cart-callout intent-danger" id="buyback-error" role="alert">
                        {getErrorMessage()}
                    </div>
                )}

                <div
                    role="radiogroup"
                    aria-describedby={hasError ? 'buyback-error' : 'buyback-required'}
                    {...(hasError && { 'aria-invalid': 'true' })}
                    aria-required="true"
                >
                    <div className="tradein-dialog__item">
                        <input
                            type="radio"
                            id="have-tradein01"
                            name="have-tradein"
                            onChange={() => {
                                setSkipBuyBack(true);
                                setAddBuyBackProducts(false);
                            }}
                            checked={skipBuyBack}
                        />
                        <label htmlFor="have-tradein01">{buyBackConfig.labels.noDeviceToTradein}</label>
                    </div>

                    <div className="tradein-dialog__item">
                        <input
                            type="radio"
                            id="have-tradein02"
                            name="have-tradein"
                            onChange={() => {
                                setSkipBuyBack(false);
                                setAddBuyBackProducts(true);
                            }}
                            checked={addBuyBackProducts}
                        />
                        <label htmlFor="have-tradein02">{buyBackConfig.labels.deviceToTradein}</label>

                        {addBuyBackProducts && !buyBackProducts.length && (
                            <div className="tradein-dialog__item-extra">
                                <RenderHTML html={buyBackConfig.labels.buyBackDialogText} nowrapper />
                                <p>
                                    <button onClick={() => dispatch(openOverlay({ name: 'buyBack' }))}>
                                        {buyBackConfig.labels.openCalculator}
                                    </button>
                                </p>
                            </div>
                        )}

                        {addBuyBackProducts && !!buyBackProducts.length && (
                            <>
                                {buyBackProducts.map((buyBackProduct) => {
                                    return (
                                        <div className="tradein-dialog__item-extra" key={buyBackProduct.model_id}>
                                            <div className="tradein-product">
                                                <div className="tradein-product__content">
                                                    <p>
                                                        <RenderHTML
                                                            html={buyBackConfig.labels.deviceValue
                                                                .replace('%1', buyBackProduct.model_name)
                                                                .replace(
                                                                    '%2',
                                                                    formatPrice(
                                                                        buyBackProduct.model_price.toString(),
                                                                        true,
                                                                    ),
                                                                )}
                                                            nowrapper
                                                        />
                                                    </p>
                                                    <p className="text-success">
                                                        <RenderHTML
                                                            html={buyBackConfig.labels.campaignBonus}
                                                            nowrapper
                                                        />
                                                    </p>
                                                </div>
                                                <button onClick={() => dispatch(openOverlay({ name: 'buyBack' }))}>
                                                    {buyBackConfig.labels.change}
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })}
                            </>
                        )}
                    </div>
                </div>
            </fieldset>
        </>
    );
});

BuyBackDetail.displayName = 'BuyBackDetail';

export default BuyBackDetail;
